/**
 * Climbing a cliff (docs/handoff/newdir/QUEUE.md 3d, data/qinggong.ts `CLIMB`): a rock face taller
 * than wall-running gets over is climbed hand over hand, slowly, for stamina. Holding still hangs on
 * (a little stamina), a ledge every few metres up lets a climber rest, and out of stamina the climber
 * falls (and the fall counts from where the hands let go: qinggong.ts `trackFall`). The World's
 * movement calls `climbCliff` when the player walks into a wall, and its qinggong tick calls
 * `holdClimb` every tick; this system keeps weapons and skills away from a climber.
 * Heights are world pixels (map.ts `PX_METRES`) like the World's `z`; the data is in metres.
 */
import { CLIMB } from '../data/qinggong';
import { floorAt, isBorder, PX_METRES, standAt, STEP_UP, Tile, tileAt, type GameMap } from '../map';
import { onCliff, type QinggongState, type QinggongStats } from '../qinggong';
import type { PlayerCommand } from '../world';
import type { SystemHost, WorldSystem } from './types';

type Climber = { x: number; y: number; z: number; vz: number; radius: number; stamina: number; climbing: boolean };

/** The top of the rock face just ahead along (ux, uy), or null when there is no climbable rock there. */
function faceTop(m: GameMap, e: Climber, ux: number, uy: number): number | null {
  const x = e.x + ux * (e.radius + 0.35);
  const y = e.y + uy * (e.radius + 0.35);
  const i = Math.floor(x);
  const j = Math.floor(y);
  if (tileAt(m, i, j) !== Tile.Rock || isBorder(m, i, j)) return null;
  return standAt(m, x, y);
}

/**
 * Walking into a rock face (dx, dy) that is too tall to wall-run (above ไต่กำแพง's `maxHeight`, or
 * `CLIMB.min` metres without it): take hold, or keep climbing, at `CLIMB.speed` for stamina. True
 * when the player climbs this tick (the World then skips the wall-run and the vault). `gravity` is
 * the World's (world pixels a second²): the climb's speed is set to hold against the next tick's.
 */
export function climbCliff(
  m: GameMap,
  e: Climber,
  st: QinggongState,
  qg: QinggongStats,
  dx: number,
  dy: number,
  gravity: number,
  dt: number,
): boolean {
  const l = Math.hypot(dx, dy) || 1;
  const [ux, uy] = [dx / l, dy / l];
  const top = faceTop(m, e, ux, uy);
  const floor = floorAt(m, e.x, e.y);
  const alt = floor + e.z;
  if (top === null || top <= alt + STEP_UP || e.stamina <= 0) return false;
  if (!onCliff(st)) {
    // Taking hold: only of a face too tall to run up, and with the stamina to start.
    if ((top - floor) * PX_METRES <= Math.max(CLIMB.min, qg.climb?.maxHeight ?? 0)) return false;
    if (e.stamina < CLIMB.start) return false;
    st.climbFoot = floor;
  }
  st.mode = 'climb';
  st.climbDX = ux;
  st.climbDY = uy;
  e.stamina = Math.max(0, e.stamina - CLIMB.stamina * dt);
  e.vz = CLIMB.speed / PX_METRES + gravity * dt;
  e.z = Math.max(e.z, 0.01);
  e.climbing = true;
  return true;
}

/**
 * Every tick on a cliff (before the movement): back on the ground lets go; turning away from the
 * face (input (dx, dy) against it), or no rock left ahead, lets go to fall; out of stamina falls
 * ('fell'). Otherwise the climber holds on: at a ledge resting (stamina back), elsewhere hanging
 * (stamina out) unless climbing on this tick (pushing into the face: `climbCliff` pays for that).
 * A jump off the face (rising faster than a climb) lets go too.
 */
export function holdClimb(
  m: GameMap,
  e: Climber & { maxStamina: number },
  st: QinggongState,
  dx: number,
  dy: number,
  gravity: number,
  dt: number,
): 'fell' | null {
  if (!onCliff(st)) return null;
  const letGo = (): void => {
    st.mode = e.z > 0 ? 'air' : 'ground';
    e.climbing = false;
  };
  // Back on the ground, or sprung off the face (a jump, ไต่กำแพง's wall-kick).
  if (e.z <= 0 || e.vz > (2 * CLIMB.speed) / PX_METRES) return letGo(), null;
  const into = dx * st.climbDX + dy * st.climbDY;
  if (into < -0.3 || faceTop(m, e, st.climbDX, st.climbDY) === null) return letGo(), null;
  if (e.stamina <= 0) return letGo(), 'fell';
  const above = (floorAt(m, e.x, e.y) + e.z - st.climbFoot) * PX_METRES;
  const k = Math.round(above / CLIMB.ledgeEvery);
  const ledge = k >= 1 && Math.abs(above - k * CLIMB.ledgeEvery) <= CLIMB.ledgeBand;
  st.mode = ledge ? 'ledge' : 'climb';
  e.climbing = true;
  if (into > 0.3) return null;
  // Holding still: the next tick's gravity is cancelled.
  e.vz = gravity * dt;
  if (ledge) e.stamina = Math.min(e.maxStamina, e.stamina + CLIMB.rest * dt);
  else e.stamina = Math.max(0, e.stamina - CLIMB.hang * dt);
  return null;
}

/** Commands a climber cannot give (both hands are on the rock). A jump stays: ไต่กำแพง's wall-kick. */
const HANDS: ReadonlySet<PlayerCommand['t']> = new Set<PlayerCommand['t']>([
  'attack',
  'skill',
  'throwHidden',
  'engage',
  'block',
  'dash',
  'meditate',
  'mount',
]);

export class ClimbSystem implements WorldSystem {
  readonly name = 'climb';

  constructor(private readonly h: SystemHost) {}

  refuse(cmd: PlayerCommand): boolean {
    if (!onCliff(this.h.world.qinggong) || !HANDS.has(cmd.t)) return false;
    if (cmd.t === 'attack' || cmd.t === 'skill') this.h.notice('มือยึดหน้าผาอยู่ ใช้วิชาไม่ได้');
    return true;
  }
}
